// frontend/App.tsx
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, Spin } from 'antd';
import { ThemeProvider } from "@/components/common/ThemeProvider";
import { useModuleRegistry } from './core/module-registry';
import { createAppRouter } from './routes';
import { AuthProvider } from '@/modules/auth/context/AuthContext';
import { NubUserModule } from './modules/nub-user';
import {AccessLevelModule} from "@/modules/nub-user/modules/access-level";
import {UserGroupModule} from "@/modules/nub-user/modules/user-group";
import {PaymentConfigModule} from "@/modules/payment-config";
import {ActionFlowModule} from "@/modules/action-flow";
import {LoanModule} from "@/modules/loans";
import {TemplateModule} from "@/modules/template";
import {SmsModule} from "@/modules/sms";
import {CustomerNotificationModule} from "@/modules/customer-notification";
import {LoanProductSettingsModule} from "@/modules/loan-product-settings";
import {DraftsModule} from "@/modules/drafts";
import {InternalAccountModule} from "@/modules/internal-account";
import {TermsAndConditionsModule} from "@/modules/termsandconditions";
import LocationModule from "@/modules/location";
import {NotificationConfigModule} from "@/modules/notification-config";
import {InAppNotificationModule} from "@/modules/in-app-notifications";
import {CustomerLoanInfoModule} from "@/modules/customer-loan-info";
import {LoanDisbursementModule} from "@/modules/loan-disbursement";
import {AuthModule} from "@/modules/auth";
import {InvitationsModule} from "@/modules/invitations";
import {LoanEligibilityConfigModule} from "@/modules/loan-eligibility-config";
import {ServiceMetricsModule} from "@/modules/service-metrics";
import {EmployersModule} from "@/modules/employers";
import {ESSMonitoringModule} from "@/modules/ess-monitoring";
import {FlowSimulatorModule} from "@/modules/flow-simulator";
import {SyncMonitoringModule} from "@/modules/sync-monitoring";
import {ESSProductManagementModule} from "@/modules/ess-product-management";
import {ESSBranchManagementModule} from "@/modules/ess-branch-management";
import {EssRepaymentNotificationsModule} from "@/modules/ess-repayment-notifications";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

export default function App() {
    const { registerModule, getAllRoutes } = useModuleRegistry();
    const [isModulesReady, setModulesReady] = useState(false);

    useEffect(() => {
        // Register all modules
        registerModule(NubUserModule);
        registerModule(AccessLevelModule);
        registerModule(UserGroupModule);
        registerModule(PaymentConfigModule);
        registerModule(ActionFlowModule);
        registerModule(TemplateModule);
        registerModule(LoanModule);
        registerModule(SmsModule);
        registerModule(CustomerNotificationModule);
        registerModule(LoanProductSettingsModule);
        registerModule(DraftsModule);
        registerModule(InternalAccountModule);
        registerModule(TermsAndConditionsModule);
        registerModule(LocationModule);
        registerModule(NotificationConfigModule);
        registerModule(InAppNotificationModule);
        registerModule(CustomerLoanInfoModule);
        registerModule(LoanDisbursementModule);
        registerModule(AuthModule);
        registerModule(InvitationsModule);
        registerModule(LoanEligibilityConfigModule);
        registerModule(ServiceMetricsModule);
        registerModule(EmployersModule);
        registerModule(ESSMonitoringModule);
        registerModule(SyncMonitoringModule);
        registerModule(FlowSimulatorModule);
        registerModule(ESSProductManagementModule);
        registerModule(ESSBranchManagementModule);
        registerModule(EssRepaymentNotificationsModule);
        setModulesReady(true);
    }, [registerModule]);

    // Only create router after modules are registered
    if (!isModulesReady) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large" />
            </div>
        );
    }

    const router = createAppRouter(getAllRoutes());

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <AuthProvider>
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: '#1890ff',
                                borderRadius: 6,
                            },
                        }}
                    >
                        <RouterProvider router={router} />
                    </ConfigProvider>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
}